import { useNotes } from "@repo/data-store";
import { asNoteId, asPatientId, type NoteId } from "@repo/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectionTab } from "@/app/surfaces/visit-note-projection/projection-tab";
import { NewNoteButton, NoteStack, NotesEmpty, noteEntries, useCreatePatientNote } from "@/features/notes";
import { NoteTabBar, useNoteTabs } from "@/features/notes/components/note-tabs";

export const Route = createFileRoute("/patients/$patientId/_layout/notes/")({
  component: RouteComponent,
  // `note` lands the caret in that note, e.g. from the overview card's "Open in Notes".
  validateSearch: (search: Record<string, unknown>): { note?: string } =>
    typeof search.note === "string" && search.note ? { note: search.note } : {},
});

function RouteComponent() {
  const { patientId: rawPatientId } = Route.useParams();
  const { note: noteParam } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patientId = asPatientId(rawPatientId);
  const { notes, isReady } = useNotes({ patientId });

  // A new note shows once the server has it, with the caret already in it.
  const { create, creating, createdId, pendingId, consumeCreated } = useCreatePatientNote(patientId);
  const entries = useMemo(() => noteEntries(notes.filter((note) => note.noteId !== pendingId)), [notes, pendingId]);

  const [focusNoteId, setFocusNoteId] = useState<NoteId | null>(null);
  useEffect(() => {
    if (!createdId) return;
    setFocusNoteId(createdId);
    consumeCreated();
  }, [createdId, consumeCreated]);

  // Consumed once, then dropped from the URL so a reload doesn't jump back to it.
  useEffect(() => {
    if (!noteParam || !isReady) return;
    setFocusNoteId(asNoteId(noteParam));
    void navigate({ search: {}, replace: true });
  }, [noteParam, isReady, navigate]);

  const clearFocus = useCallback(() => setFocusNoteId(null), []);
  const noteTabs = useNoteTabs(patientId);

  // An empty list is not a fact until the collection has answered.
  if (!isReady) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <NoteTabBar
        patientId={patientId}
        tabs={noteTabs.tabs}
        active={noteTabs.active}
        onActivate={noteTabs.activate}
        onClose={noteTabs.close}
        onOpen={(visitId) => noteTabs.open(visitId)}
      />
      {noteTabs.activeTab ? (
        <ProjectionTab key={noteTabs.activeTab.visitId} patientId={patientId} visitId={noteTabs.activeTab.visitId} />
      ) : entries.length === 0 ? (
        <NotesEmpty>
          <NewNoteButton onClick={create} pending={creating} />
        </NotesEmpty>
      ) : (
        <div className="min-h-0 flex-1">
          <NoteStack
            patientId={patientId}
            entries={entries}
            focusNoteId={focusNoteId}
            onFocusConsumed={clearFocus}
            onAddNote={create}
            addingNote={creating}
            onOpenProjection={(visitId) => noteTabs.open(visitId)}
          />
        </div>
      )}
    </div>
  );
}
